import { useEffect, useMemo, useRef, useState } from 'react';
import type { DataISO, Dipendente, Evento, GiornoCalendario } from '../api/tipi.ts';
import type { Verificatore } from '../api/verifica.ts';
import type { NuovaRichiesta } from '../api/client.ts';
import { ORARIO_TEMPO_PIENO } from '../analisi/calcoli.ts';
import { PannelloRichiesta } from '../richiesta/PannelloRichiesta.tsx';
import { BarraCalendario } from './BarraCalendario.tsx';
import { GrigliaCalendario } from './GrigliaCalendario.tsx';
import {
  FILTRI_VUOTI,
  type Filtri,
  type Periodo,
  dentroLAnno,
  filtraDipendenti,
  finestra,
  inForzaNellaFinestra,
  giorniNellaFinestra,
  scorri,
  trovaDipendente,
} from './controlli.ts';

/**
 * Il calendario con i suoi controlli.
 *
 * Il periodo funziona come in un calendario da ufficio: settimana, mese o anno
 * intero, con le frecce per scorrere. La settimana e' il modo in cui si
 * pianifica davvero — 7 colonne larghe si leggono, 365 si scorrono — mentre
 * l'anno intero resta per il colpo d'occhio su chi e' via e quando.
 */

export interface ProprietaPaginaCalendario {
  anno: number;
  giorni: readonly GiornoCalendario[];
  dipendenti: readonly Dipendente[];
  eventi: readonly Evento[];
  dipendentiModificabili: ReadonlySet<string>;
  /** Chi sta guardando: il calendario si apre gia' sulla sua riga. */
  dipendenteProprio?: string | null;
  /** Se offrire il tipo "Riposo produzione": lo registra solo chi ha delega. */
  puoRiposoProduzione: boolean;
  /** Ultimo giorno prenotabile: il tetto dei campi data del pannello. */
  ultimoGiornoInseribile: DataISO;
  /** Primo giorno scrivibile: oggi, o il primo gennaio per chi corregge il passato. */
  primoGiornoInseribile: DataISO;
  verifica: Verificatore;
  onInvia: (richiesta: NuovaRichiesta) => Promise<void>;
  /** Annulla un evento gia' registrato. Assente = il calendario e' in sola scrittura. */
  onAnnulla?: (evento: Evento) => Promise<void>;
}

export function PaginaCalendario({
  anno,
  giorni,
  dipendenti,
  eventi,
  dipendentiModificabili,
  dipendenteProprio = null,
  puoRiposoProduzione,
  ultimoGiornoInseribile,
  primoGiornoInseribile,
  verifica,
  onInvia,
  onAnnulla,
}: ProprietaPaginaCalendario) {
  const oggi = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Rome' });

  const [periodo, setPeriodo] = useState<Periodo>('settimana');
  const [ancora, setAncora] = useState<DataISO>(oggi);

  /*
   * L'ancoraggio va riportato dentro l'anno a ogni disegno, non solo quando
   * la pagina nasce.
   *
   * Cambiando anno l'ancoraggio veniva ricalcolato al rimontaggio, ma in quel
   * momento i dati caricati erano ancora quelli dell'anno precedente: l'anno
   * che la pagina riceveva era il vecchio, l'ancoraggio restava a oggi, e
   * quando arrivava il nuovo la settimana e il mese continuavano a puntare
   * all'anno di prima - finestra fuori dai giorni caricati, griglia vuota.
   * La vista Anno non se ne accorgeva, perche' e' l'unica che non guarda
   * l'ancoraggio.
   *
   * Calcolandolo qui, qualunque anno arrivi, la finestra e' sempre dentro.
   */
  const ancoraViva = dentroLAnno(anno, ancora);

  // Serve solo a far ripartire il centraggio su oggi: cambia a ogni clic sul
  // pulsante, e la griglia se ne accorge. Il valore non conta.
  const [richiesteOggi, setRichiesteOggi] = useState(0);

  /*
   * Cambiando anno la pagina non riparte da capo: filtri e ricerca restano.
   *
   * Dove ci si posiziona dipende da quale anno si sceglie, perche' sono due
   * gesti diversi. Un anno futuro si apre per pianificare, e si comincia da
   * gennaio: la vista passa al mese, che e' la grana con cui si guarda un
   * anno che non e' ancora cominciato. Tornando all'anno in corso si torna al
   * lavoro di tutti i giorni, quindi a oggi, e la vista resta quella che si
   * stava usando.
   */
  const annoMostrato = useRef(anno);
  useEffect(() => {
    if (annoMostrato.current === anno) return;
    annoMostrato.current = anno;

    // Il pannello aperto riguarda un giorno dell'anno di prima: lasciarlo
    // aperto significherebbe inserire una richiesta su una data che non si
    // vede piu'.
    setRichiesta(null);

    if (anno === Number(oggi.slice(0, 4))) {
      setAncora(oggi);
      return;
    }
    setAncora(`${anno}-01-01`);
    setPeriodo('mese');
  }, [anno, oggi]);
  const [filtri, setFiltri] = useState<Filtri>(FILTRI_VUOTI);
  const [richiesta, setRichiesta] = useState<{ dipendenteId: string; giorno: DataISO } | null>(
    null,
  );

  const f = useMemo(() => finestra(periodo, ancoraViva, anno), [periodo, ancoraViva, anno]);
  const giorniVisibili = useMemo(() => giorniNellaFinestra(giorni, f), [giorni, f]);
  // Chi non era in forza in nessun giorno della finestra non compare: tre
  // righe vuote in mezzo ai colleghi sono solo rumore. Resta pero' visibile
  // guardando i mesi in cui lavorava davvero.
  const visibili = useMemo(
    () => filtraDipendenti(dipendenti, filtri).filter((d) => inForzaNellaFinestra(d, f)),
    [dipendenti, filtri, f],
  );

  // La ricerca evidenzia e posiziona, non filtra: si vuole vedere quella
  // persona senza perdere di vista chi altro e' assente negli stessi giorni.
  const indiceTrovato = useMemo(
    () => trovaDipendente(visibili, filtri.ricerca),
    [visibili, filtri.ricerca],
  );
  const risultati = useMemo(
    () =>
      filtri.ricerca === ''
        ? 0
        : visibili.filter((d) =>
            d.nomeCompleto.toLowerCase().includes(filtri.ricerca.toLowerCase().trim()),
          ).length,
    [visibili, filtri.ricerca],
  );

  /*
   * Gli eventi della persona di cui e' aperta la cella.
   *
   * Non filtrati per giorno: a quello pensa il pannello, sulla data che ha
   * nel modulo, cosi' l'elenco segue chi cambia la data invece di restare
   * fermo al giorno cliccato.
   *
   * Si ricalcolano dagli eventi invece di essere fotografati al click: dopo
   * un annullamento la pagina ricarica i dati, e un elenco congelato
   * continuerebbe a mostrare l'evento appena tolto con accanto il suo
   * pulsante Annulla.
   *
   * Annullati e rifiutati restano fuori: non occupano il giorno, ed e' lo
   * stesso criterio con cui la griglia decide cosa colorare.
   */
  const eventiDellaPersona = useMemo(() => {
    if (!richiesta) return [];
    return eventi.filter(
      (e) =>
        e.dipendenteId === richiesta.dipendenteId &&
        e.stato !== 'ANNULLATO' &&
        e.stato !== 'RIFIUTATO',
    );
  }, [eventi, richiesta]);

  const destinatario =
    richiesta && dipendenti.find((d) => d.id === richiesta.dipendenteId);
  const nomeRichiesto = destinatario?.nomeCompleto;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <BarraCalendario
        periodo={periodo}
        etichettaPeriodo={f.etichetta}
        filtri={filtri}
        dipendenti={dipendenti}
        risultatiRicerca={risultati}
        onPeriodo={setPeriodo}
        onScorri={(passi) => setAncora(dentroLAnno(anno, scorri(periodo, ancoraViva, passi)))}
        onOggi={() => {
          // Due gesti in un pulsante solo: porta la finestra sul giorno di
          // oggi, e chiede alla griglia di centrarne la colonna. Sono le due
          // meta' che prima stavano su due pulsanti diversi, e quello dentro
          // la griglia non poteva funzionare quando oggi cadeva fuori dalla
          // finestra mostrata.
          setAncora(dentroLAnno(anno, oggi));
          setRichiesteOggi((n) => n + 1);
        }}
        onFiltri={setFiltri}
      />

      <div style={{ flex: 1, minHeight: 0 }}>
        <GrigliaCalendario
          // Rimontarla riporta lo scorrimento a inizio periodo e la riposiziona
          // sulla riga di chi guarda: senza, passando al 2027 si resterebbe
          // all'altezza di scorrimento di settembre, su un gennaio che di
          // colonne ne ha meno.
          key={anno}
          giorni={giorniVisibili}
          dipendenti={visibili}
          eventi={eventi}
          raggruppaPerSede
          dipendentiModificabili={dipendentiModificabili}
          dipendenteProprio={dipendenteProprio}
          ricerca={filtri.ricerca}
          centraSuOggi={richiesteOggi}
          dipendenteDaMostrare={indiceTrovato >= 0 ? (visibili[indiceTrovato]?.id ?? null) : null}
          onApriCella={(cella) => {
            // Si apre sempre, anche su una giornata piena.
            //
            // Prima il click non faceva niente quando c'erano gia' sia
            // un'assenza sia una trasferta - ed era esattamente la cella che
            // si vuole sistemare quando qualcosa e' stato registrato male.
            // Il pannello mostra quello che c'e', con il suo Annulla, e il
            // modulo di inserimento sotto: e' il "cancella e riscrivi" fatto
            // in un posto solo.
            setRichiesta({ dipendenteId: cella.dipendenteId, giorno: cella.data });
          }}
        />
      </div>

      {richiesta && (
        <PannelloRichiesta
          dipendenteId={richiesta.dipendenteId}
          nomeDipendente={nomeRichiesto ?? 'Dipendente'}
          giornoIniziale={richiesta.giorno}
          puoRiposoProduzione={puoRiposoProduzione}
          ultimoGiornoInseribile={ultimoGiornoInseribile}
          primoGiornoInseribile={primoGiornoInseribile}
          eventiDellaPersona={eventiDellaPersona}
          {...(onAnnulla !== undefined ? { onAnnulla } : {})}
          orarioSettimanale={destinatario?.orarioSettimanale ?? ORARIO_TEMPO_PIENO}
          verifica={verifica}
          onChiudi={() => setRichiesta(null)}
          onInvia={async (dati) => {
            await onInvia({ dipendenteId: richiesta.dipendenteId, ...dati });
            setRichiesta(null);
          }}
        />
      )}
    </div>
  );
}
